import { useEffect, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { CalendarClock, Check, Loader2, ShieldCheck, X } from 'lucide-react';
import Aide from './Aide';
import LienFederation from './LienFederation';
import { AIDES_ADHERENTS } from '../utils/aides';
import {
  enregistrerImmatriculation,
  fermerDiscipline,
  type DisciplineCatalogue,
  type PresetClub,
} from '../services/api';

/**
 * La fiche de la fédération adoptée par le club.
 *
 * POURQUOI CET ENCART EXISTE
 * ---------------------------------------------------------------------------
 * C'est la page qu'on rouvrira dans six mois pour répondre à « nos catégories
 * viennent d'où ? » — devant un parent qui conteste le groupe de son enfant,
 * devant un contrôle de la fédération, ou simplement devant le bureau
 * renouvelé qui n'était pas là au moment du choix.
 *
 * Tant qu'on essaie des disciplines, ce qui compte est de les COMPARER : c'est
 * le rôle des quinze cartes. Une fois choisie, ce qui compte est de savoir
 * précisément à quoi l'on s'est rattaché.
 *
 * CE QU'IL AFFICHAIT AVANT, ET CE QUI A CHANGÉ
 * ---------------------------------------------------------------------------
 * Il ne paraissait qu'une fois le préréglage verrouillé à la main. Ce verrou a
 * été retiré : il demandait qu'on y pense, alors que la saison fait le travail
 * toute seule. L'encart s'affiche donc dès qu'une discipline est en service, et
 * dit en tête si la saison la tient — avec la date à laquelle elle la relâche.
 */
export default function FicheFederation({
  preset,
  discipline,
  saisonOuverte,
  motifSaison,
  fermable,
}: {
  preset: PresetClub;
  /** La fiche du catalogue : elle porte ce que le préréglage du club ne garde pas. */
  discipline: DisciplineCatalogue | undefined;
  saisonOuverte: boolean;
  motifSaison: string | null;
  /**
   * Peut-on retirer cette discipline ?
   *
   * Faux quand c'est la seule : la fermer laisserait le registre sans
   * catégories du tout. Dans ce cas on n'affiche pas un bouton grisé — on
   * n'affiche rien, parce qu'un club mono-discipline ne s'est jamais posé la
   * question et qu'un bouton mort ne fait que l'inquiéter.
   */
  fermable: boolean;
}) {
  const tenue = !saisonOuverte && motifSaison !== null;

  return (
    <section className="rounded-xl border border-primary-300 dark:border-primary-800 bg-primary-50/60 dark:bg-primary-950/20 p-4 space-y-3">
      <header className="flex items-start gap-2">
        {tenue ? (
          <CalendarClock className="w-5 h-5 shrink-0 text-primary-700 dark:text-primary-400 mt-0.5" />
        ) : (
          <ShieldCheck className="w-5 h-5 shrink-0 text-primary-700 dark:text-primary-400 mt-0.5" />
        )}
        <div className="min-w-0">
          <h3 className="font-semibold text-gray-900 dark:text-gray-100">
            {preset.nom}
            {tenue ? ' — figée jusqu’à la fin de la saison' : ''}
          </h3>
          <p className="text-sm text-gray-700 dark:text-gray-300 mt-0.5">{preset.federation}</p>
          {/*
            Sous le nom de la fédération, et non dans un menu : c'est ici
            qu'on lit « FFJDA » et qu'on se demande d'où viennent les
            catégories. La réponse doit être à portée du regard qui pose la
            question.
          */}
          <LienFederation url={preset.reglementation} className="mt-1" />
        </div>

        {fermable && <BoutonFermer preset={preset} />}
      </header>

      {/*
        Le motif de la saison, dit ici et pas ailleurs.

        Le mettre au-dessus de la liste des disciplines l'aurait séparé de ce à
        quoi il se rapporte — la discipline en service. Ici, on lit d'un coup
        laquelle c'est et pourquoi elle ne bouge pas.
      */}
      {tenue && (
        <p className="text-sm text-primary-900 dark:text-primary-200 border-t border-primary-200 dark:border-primary-900 pt-2">
          {motifSaison}
          <span className="block text-xs text-primary-800/80 dark:text-primary-300/80 mt-1">
            Vous pouvez toujours renommer, ajouter ou retirer ce que contient votre configuration :
            ces gestes-là ne déplacent personne.
          </span>
        </p>
      )}

      {/*
        Ce que le préréglage a posé, en chiffres.

        Cet inventaire vivait dans un second encart, juste au-dessus, qui
        répétait déjà le nom de la discipline, sa fédération, sa saison et ses
        mentions réglementaires. Deux boîtes qui disent les mêmes choses valent
        moins qu'une : on les lit en diagonale, en croyant que la seconde
        apporte autre chose.
      */}
      <ul className="flex flex-wrap gap-x-4 gap-y-1 text-sm text-primary-900 dark:text-primary-200 border-t border-primary-200 dark:border-primary-900 pt-2">
        {compter(preset)
          .filter(([n]) => n > 0)
          .map(([n, singulier, pluriel]) => (
            <li key={singulier}>
              <strong>{n}</strong> {n > 1 ? pluriel : singulier}
            </li>
          ))}
      </ul>

      <Immatriculation preset={preset} />

      {discipline !== undefined && (
        <dl className="grid gap-x-6 gap-y-2 sm:grid-cols-2 text-sm border-t border-primary-200 dark:border-primary-900 pt-2">
          <Ligne titre="Pratique" valeur={discipline.typePratique} />
          <Ligne titre="Saison" valeur={discipline.saison} />
          {discipline.competitions.length > 0 && (
            <Ligne titre="Compétitions" valeur={discipline.competitions.join(', ')} />
          )}
          {discipline.infrastructures.length > 0 && (
            <Ligne titre="Lieux de pratique" valeur={discipline.infrastructures.join(', ')} />
          )}
        </dl>
      )}

      {discipline?.note != null && discipline.note !== '' && (
        <p className="text-sm text-gray-700 dark:text-gray-300 border-t border-primary-200 dark:border-primary-900 pt-2">
          {discipline.note}
        </p>
      )}

      {preset.mentions.length > 0 && (
        <div className="border-t border-primary-200 dark:border-primary-900 pt-2">
          <p className="text-xs font-semibold uppercase tracking-wide text-primary-800/70 dark:text-primary-300/70">
            À rappeler à vos licenciés
          </p>
          <ul className="mt-1 space-y-0.5 text-sm text-gray-700 dark:text-gray-300">
            {preset.mentions.map((mention) => (
              <li key={mention}>• {mention}</li>
            ))}
          </ul>
        </div>
      )}
    </section>
  );
}

/**
 * Ce que le préréglage a posé, compté.
 *
 * LE PLURIEL EST ÉCRIT, PAS FABRIQUÉ.
 *
 * Ajouter un « s » à la fin d'un libellé le pose sur le DERNIER mot :
 * « 4 fonction d'encadrements », « 5 champ de fiches », « 2 modèle
 * d'événements ». En français c'est le premier qui s'accorde, et une règle qui
 * devine se trompe dès qu'un libellé fait plus d'un mot.
 *
 * @return list<array{0: number, 1: string, 2: string}>
 */
function compter(preset: PresetClub): Array<[number, string, string]> {
  return [
    [preset.categories.length, 'catégorie', 'catégories'],
    [preset.postes.length, 'poste', 'postes'],
    [preset.encadrants.length, "fonction d'encadrement", "fonctions d'encadrement"],
    [preset.champs.length, 'champ de fiche', 'champs de fiche'],
    [preset.evenements.length, "modèle d'événement", "modèles d'événement"],
    [preset.documents.length, 'courrier type', 'courriers types'],
  ];
}

function Ligne({ titre, valeur }: { titre: string; valeur: string }) {
  return (
    <div>
      <dt className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
        {titre}
      </dt>
      <dd className="text-gray-800 dark:text-gray-200">{valeur}</dd>
    </div>
  );
}

/**
 * Le numéro du club auprès de cette fédération.
 *
 * POURQUOI IL EST ICI, ET PAS DANS LES PARAMÈTRES DU CLUB
 * ---------------------------------------------------------------------------
 * Un club omnisports en a un par fédération : le football le connaît sous un
 * numéro de district, la gymnastique sous un autre, le judo sous un troisième.
 * Un champ unique dans les paramètres généraux aurait été faux dès la deuxième
 * discipline — et faux silencieusement, ce qui est pire : le numéro serait
 * parti sur des feuilles de match et des demandes de subvention sans que
 * personne ne le remarque.
 *
 * Il vit donc dans l'encart de SA fédération, à côté du nom de celle-ci.
 *
 * POURQUOI IL S'ENREGISTRE À LA SORTIE DU CHAMP
 * ---------------------------------------------------------------------------
 * Pas de bouton « Enregistrer » : un champ isolé avec son propre bouton se
 * remplit, puis se quitte sans cliquer, et le numéro est perdu sans un mot. La
 * sortie du champ suffit, et le retour du serveur le confirme à l'écran.
 */
function Immatriculation({ preset }: { preset: PresetClub }) {
  const client = useQueryClient();
  const [saisi, setSaisi] = useState(preset.immatriculation ?? '');
  const [enregistre, setEnregistre] = useState(false);
  const [erreur, setErreur] = useState<string | null>(null);

  /*
    Le champ suit le serveur quand celui-ci change sous lui.
    Sans cela, appliquer une autre discipline laisserait l'ancien numéro
    affiché dans une fiche qui n'est plus la sienne.
  */
  useEffect(() => {
    setSaisi(preset.immatriculation ?? '');
  }, [preset.id, preset.immatriculation]);

  const envoi = useMutation({
    mutationFn: (valeur: string) => enregistrerImmatriculation(preset.id, valeur),
    onSuccess: (reponse) => {
      if (reponse.success === false) {
        setErreur(reponse.message ?? "Ce numéro n'a pas pu être enregistré.");
        return;
      }
      setErreur(null);
      setEnregistre(true);
      void client.invalidateQueries({ queryKey: ['preset-club'] });
    },
    onError: (e: Error) => setErreur(e.message),
  });

  const identifiant = `immatriculation-${preset.id}`;
  const inchange = saisi.trim() === (preset.immatriculation ?? '');

  return (
    <div className="border-t border-primary-200 dark:border-primary-900 pt-2">
      <div className="flex items-center gap-1.5">
        <label
          htmlFor={identifiant}
          className="block text-xs font-semibold uppercase tracking-wide text-primary-800/70 dark:text-primary-300/70"
        >
          Votre numéro d’affiliation à {preset.federation}
        </label>
        {/*
          L'aide est ici, et pas dans un guide.

          « Numéro d'affiliation » se comprend ; ce qui ne se devine pas, c'est
          qu'il y en a UN PAR FÉDÉRATION, et qu'il se retrouve dans les
          courriers types sous un repère. Une explication qu'il faut aller
          chercher dans un guide n'est pas lue au moment où l'on remplit le
          champ, c'est-à-dire au seul moment où elle sert.
        */}
        {AIDES_ADHERENTS['Numéro d’affiliation'] !== undefined && (
          <Aide titre="Numéro d’affiliation">{AIDES_ADHERENTS['Numéro d’affiliation']}</Aide>
        )}
      </div>

      <div className="mt-1 flex items-center gap-2">
        <input
          id={identifiant}
          type="text"
          value={saisi}
          maxLength={80}
          placeholder="Non renseigné"
          onChange={(e) => {
            setSaisi(e.target.value);
            setEnregistre(false);
            setErreur(null);
          }}
          onBlur={() => {
            if (!inchange) {
              envoi.mutate(saisi.trim());
            }
          }}
          className="w-full max-w-xs rounded-lg border border-primary-200 dark:border-primary-800 bg-white dark:bg-gray-900 px-2 py-1.5 text-sm text-gray-900 dark:text-gray-100 min-h-[44px]"
        />

        {envoi.isPending && <Loader2 className="w-4 h-4 animate-spin text-primary-600 shrink-0" />}
        {enregistre && !envoi.isPending && (
          <Check className="w-4 h-4 text-green-600 shrink-0" aria-label="enregistré" />
        )}
      </div>

      <p className="mt-1 text-xs text-gray-600 dark:text-gray-400">
        C’est ce numéro qui part sur vos feuilles de match, vos demandes de subvention et vos
        courriers à la ligue. Chaque fédération a le sien.
      </p>

      {erreur !== null && <p className="mt-1 text-xs text-red-700 dark:text-red-400">{erreur}</p>}
    </div>
  );
}

/**
 * Retirer une discipline du registre.
 *
 * FERMER N'EST PAS DÉTRUIRE, ET LE BOUTON DOIT LE DIRE
 * ---------------------------------------------------------------------------
 * Une discipline fermée sort des écrans d'inscription : on ne peut plus y
 * licencier personne. Les licences déjà prises restent, avec leurs règlements
 * et leurs reçus — c'est l'exercice comptable de l'an dernier, on ne l'efface
 * pas. Et elle se rouvre en la choisissant de nouveau, avec les catégories
 * telles que le club les avait renommées.
 *
 * Le mot « supprimer » aurait donc menti dans les deux sens : trop grave pour
 * ce que le geste fait, et trompeur sur ce qu'on peut défaire.
 *
 * La confirmation tient en un second clic sur le même bouton plutôt qu'en une
 * fenêtre : le geste est réversible, et une fenêtre modale pour un geste
 * réversible apprend à cliquer « oui » sans lire.
 */
function BoutonFermer({ preset }: { preset: PresetClub }) {
  const client = useQueryClient();
  const [confirme, setConfirme] = useState(false);
  const [refus, setRefus] = useState<string | null>(null);

  const fermeture = useMutation({
    mutationFn: () => fermerDiscipline(preset.id),
    onSuccess: (reponse) => {
      if (reponse.success === false) {
        // Le serveur refuse quand des licenciés de la saison en cours y sont
        // inscrits, ou quand c'est la dernière. Il dit pourquoi ; on le montre.
        setRefus(reponse.message ?? 'Cette discipline ne peut pas être fermée.');
        setConfirme(false);
        return;
      }
      setRefus(null);
      setConfirme(false);
      void client.invalidateQueries({ queryKey: ['preset-club'] });
      void client.invalidateQueries({ queryKey: ['glossaire-sportif'] });
      void client.invalidateQueries({ queryKey: ['adherents-configuration'] });
    },
    onError: (e: Error) => {
      setRefus(e.message);
      setConfirme(false);
    },
  });

  return (
    <div className="ml-auto shrink-0 text-right">
      <button
        type="button"
        disabled={fermeture.isPending}
        onClick={() => {
          if (confirme) {
            fermeture.mutate();
          } else {
            setConfirme(true);
            setRefus(null);
          }
        }}
        onBlur={() => setConfirme(false)}
        className={`inline-flex items-center gap-1.5 rounded-lg border px-2.5 py-1.5 text-xs font-medium min-h-[44px] ${
          confirme
            ? 'border-amber-400 bg-amber-50 text-amber-900 dark:bg-amber-950/30 dark:text-amber-200'
            : 'border-primary-200 dark:border-primary-800 text-primary-800 dark:text-primary-300 hover:bg-primary-100/60 dark:hover:bg-primary-900/30'
        } disabled:opacity-60`}
      >
        {fermeture.isPending ? (
          <Loader2 className="w-3.5 h-3.5 animate-spin" />
        ) : (
          <X className="w-3.5 h-3.5" aria-hidden="true" />
        )}
        {confirme ? 'Confirmer la fermeture' : 'Fermer cette discipline'}
      </button>

      {confirme && !fermeture.isPending && (
        <p className="mt-1 max-w-[16rem] text-xs text-amber-800 dark:text-amber-300">
          On ne pourra plus y licencier personne. Les licences déjà prises restent lisibles, et vous
          pourrez la rouvrir quand vous voudrez.
        </p>
      )}

      {refus !== null && (
        <p className="mt-1 max-w-[16rem] text-xs text-red-700 dark:text-red-400">{refus}</p>
      )}
    </div>
  );
}
